import _find from 'lodash/find';
import _get from 'lodash/get';
// Permissions
import permissions from '../constants/Permissions';
// Navigation modules used for paths building
// App
import { listPath as installationsListPath } from '../routes/app/routes/installations/navigation';
import { listPath as devicesListPath } from '../routes/app/routes/devices/navigation';
import { listPath as usersListPath } from '../routes/app/routes/users/navigation';
// CPM
import { listPath as carparksListPath } from '../routes/cpm/routes/carparks/navigation';
import { listPath as accountsListPath } from '../routes/cpm/routes/accounts/navigation';
// CPM CLIENT
import { bookCarparksListPath } from '../routes/book/navigation';

/*
** From Webpack Env Config
*/
const PRODUCT = __PRODUCT__; // eslint-disable-line no-undef

// Get needed permissions
const {
  pInstallation: { read: installationRead },
  pDevice: { read: deviceRead },
  pAdmin,
  pFeature,
} = permissions;

// Bellow paths are used to redirect user after login
// Order determines which view will be loaded first according with user permissions
// App paths
const appPaths = [
  { path: installationsListPath(), permission: `${pAdmin}||${installationRead}` },
  { path: devicesListPath(), permission: `${pAdmin}||${deviceRead}` },
  { path: usersListPath(), permission: pAdmin },
];
// CPM paths
const cpmManagerPaths = [
  { path: carparksListPath(), permission: `${pFeature.admin}||${pFeature.carparks}` },
  { path: accountsListPath(), permission: `${pFeature.admin}||${pFeature.accounts}` },
];
// CPM CLIENT paths
const cpmClientPaths = [
  { path: bookCarparksListPath(), permission: pFeature.bookings },
];

/**
* Lists
*/
const productsList = [
  {
    key: 'opac',
    value: 'opac',
    labelId: 'product.opac',
    icon: 'productOpac',
    langFileName: 'opac',
    config: {
      useGraphQL: false,
      loadOperators: true,
      withMeAlertings: true,
      withMeCallbacks: true,
      withMePaymentMethods: false,
      withSidebar: true,
      withNotifications: true,
      withProductName: true,
    },
    paths: appPaths,
    context: 'userPermissions',
    authorizedLocales: ['en-GB', 'fr-FR'],
    tradFiles: ['common.json', 'opac.json'],
    usedRoutes: [
      '404',
      '500',
      'account',
      'app',
      'error',
      'login',
      'recoverPassword',
    ],
  },
  {
    key: 'cpm_manager',
    value: 'cpm_manager',
    labelId: 'product.carparkManagement',
    icon: 'productParking',
    langFileName: 'cpm-manager',
    config: {
      useGraphQL: true,
      loadOperators: false,
      withMeAlertings: false,
      withMeCallbacks: false,
      withMePaymentMethods: false,
      withSidebar: false,
      withNotifications: true,
      withProductName: false,
    },
    paths: cpmManagerPaths,
    context: 'meFeatures',
    authorizedLocales: ['en-GB', 'fr-FR'],
    tradFiles: ['common.json', 'cpm-manager.json'],
    usedRoutes: [
      '404',
      '500',
      'account', 'cpm', 'error', 'login',
      'recoverPassword',
    ],
  },
  {
    key: 'cpm_client',
    value: 'cpm_client',
    labelId: 'product.carparkManagement',
    icon: 'productParking',
    langFileName: 'cpm-client',
    config: {
      useGraphQL: true,
      loadOperators: false,
      withMeAlertings: false,
      withMeCallbacks: false,
      withMePaymentMethods: true,
      withSidebar: false,
      withNotifications: true,
      withProductName: false,
    },
    paths: cpmClientPaths,
    context: 'meFeatures',
    authorizedLocales: ['en-GB', 'es-ES', 'fr-FR'],
    tradFiles: ['common.json', 'cpm-client.json'],
    usedRoutes: [
      '404',
      '500',
      'account',
      'checkout',
      'book',
      'error',
      'login',
      'more',
      'my_bookings',
      'my_on_demand_access',
      'purchase',
      'recoverPassword',
      'validateAccount',
    ],
  },
];

const configurationProductsList = [
  {
    key: 'opac',
    value: 'opac',
    labelId: 'product.opac',
    icon: 'productOpac',
    langFileName: 'opac',
  },
  {
    key: 'car_park_management',
    value: 'car_park_management',
    labelId: 'product.carparkManagement',
    icon: 'productParking',
    langFileName: 'cpm',
  },
];

/**
* Utils
*/
const productFromValue = (productValue = PRODUCT) => _find(productsList, ['value', productValue]);
const productFromConfigurationValue = (value) => _find(configurationProductsList, ['value', value]);

const productGetLangFileName = (productValue = PRODUCT) => _get(productFromValue(productValue), 'langFileName', {});
const productGetConfig = (productValue = PRODUCT) => _get(productFromValue(productValue), 'config', {});
const productGetPaths = (productValue = PRODUCT) => _get(productFromValue(productValue), 'paths', {});
const productGetContext = (productValue = PRODUCT) => _get(productFromValue(productValue), 'context', {});
const productGetOpacValue = productsList[0].value;
const productGetCpmManagerValue = productsList[1].value;
const productGetCpmClientValue = productsList[2].value;
const configurationProductGetCpmValue = configurationProductsList[1].value;
const productGetUsedRoutes = (productValue = PRODUCT) => _get(productFromValue(productValue), 'usedRoutes', {});
const productGetAuthorizedLocales = (productValue = PRODUCT) => _get(productFromValue(productValue), 'authorizedLocales', []);
const productGetTradFilesNames = (productValue = PRODUCT) => _get(productFromValue(productValue), 'tradFiles', {});

const isCpmClient = (productValue = PRODUCT) => productValue === productGetCpmClientValue;
const isCpmManager = (productValue = PRODUCT) => productValue === productGetCpmManagerValue;
const isOpac = (productValue = PRODUCT) => productValue === productGetOpacValue;

/**
* Model
*/
const product = {
  // Name and icon
  name: 'product',
  icon: null,

  // Lists
  list: productsList,
  configurationList: configurationProductsList,

  // Utils
  fromValue: productFromValue,
  fromConfigurationValue: productFromConfigurationValue,
  getLangFileName: productGetLangFileName,
  getConfig: productGetConfig,
  getPaths: productGetPaths,
  getOpacValue: productGetOpacValue,
  getCpmManagerValue: productGetCpmManagerValue,
  getCpmClientValue: productGetCpmClientValue,
  getConfigurationCpmValue: configurationProductGetCpmValue,
  getUsedRoutes: productGetUsedRoutes,
  getAuthorizedLocales: productGetAuthorizedLocales,
  getTradFilesNames: productGetTradFilesNames,
  getContext: productGetContext,
  isCpmClient,
  isCpmManager,
  isOpac,
};

module.exports = product;



// WEBPACK FOOTER //
// ./models/product.js